import { getDreamBotRuntimeConfig } from './runtimeConfig';

type TrackProps = Record<string, unknown>;

/**
 * Offline queue for analytics events
 */
const QUEUE_KEY = 'dreambot_analytics_queue';
const MAX_QUEUE_SIZE = 100;

let isOnline = typeof navigator !== 'undefined' ? navigator.onLine : true;
let isProcessingQueue = false;

// Listen for online/offline events
if (typeof window !== 'undefined') {
  window.addEventListener('online', () => {
    isOnline = true;
    processQueue();
  });
  
  window.addEventListener('offline', () => {
    isOnline = false;
  });
}

/**
 * Get queued events from localStorage
 */
const getQueue = (): DreamBotEventPayload[] => {
  try {
    const stored = localStorage.getItem(QUEUE_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
};

/**
 * Save events to queue
 */
const saveToQueue = (event: DreamBotEventPayload) => {
  try {
    const queue = getQueue();
    queue.push(event);
    
    // Limit queue size to prevent storage issues
    if (queue.length > MAX_QUEUE_SIZE) {
      queue.shift();
    }
    
    localStorage.setItem(QUEUE_KEY, JSON.stringify(queue));
  } catch {
    // Ignore storage errors
  }
};

/**
 * Process queued events
 */
const processQueue = async () => {
  if (isProcessingQueue || !isOnline) return;
  
  isProcessingQueue = true;
  const queue = getQueue();
  
  if (queue.length === 0) {
    isProcessingQueue = false;
    return;
  }
  
  const cfg = getDreamBotRuntimeConfig();
  const eventPath = cfg.eventPath || 'event';
  const url = buildApiUrl(eventPath);
  
  // Process events one by one
  for (const event of queue) {
    try {
      await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(cfg.embedToken ? { 'X-DreamBot-Token': cfg.embedToken } : {}),
        },
        body: JSON.stringify(event),
        keepalive: true,
      });
    } catch {
      // If sending fails, stop processing and keep remaining events
      break;
    }
  }
  
  // Remove successfully sent events
  try {
    const remainingQueue = getQueue();
    if (remainingQueue.length < queue.length) {
      localStorage.setItem(QUEUE_KEY, JSON.stringify(remainingQueue.slice(queue.length - remainingQueue.length)));
    } else {
      localStorage.removeItem(QUEUE_KEY);
    }
  } catch {
    // Ignore
  }
  
  isProcessingQueue = false;
};

type DreamBotEventPayload = {
  site_id: string;
  session_id: string;
  event_name: string;
  metadata: TrackProps;
};

const getSessionId = () => {
  const key = 'dreambot_session_id';
  try {
    const existing = sessionStorage.getItem(key);
    if (existing && typeof existing === 'string') return existing;

    const id =
      (globalThis.crypto && 'randomUUID' in globalThis.crypto && typeof globalThis.crypto.randomUUID === 'function')
        ? globalThis.crypto.randomUUID()
        : String(Date.now()) + '-' + Math.random().toString(16).slice(2);

    sessionStorage.setItem(key, id);
    return id;
  } catch {
    return String(Date.now()) + '-' + Math.random().toString(16).slice(2);
  }
};

const buildApiUrl = (path: string) => {
  // Use Cloudflare Worker URL directly
  const workerBaseUrl = 'https://dreambot-gateway.dev-c2d.workers.dev';
  return `${workerBaseUrl}${path}`;
};

export async function trackEvent(event: string, props?: TrackProps) {
  const url = buildApiUrl('/event');
  const { siteId } = getDreamBotRuntimeConfig();

  const payload: DreamBotEventPayload = {
    site_id: siteId || 'mattressunboxedco',
    session_id: getSessionId(),
    event_name: event,
    metadata: props || {},
  };

  // If offline, queue the event
  if (!isOnline) {
    saveToQueue(payload);
    return;
  }

  try {
    const { embedToken } = getDreamBotRuntimeConfig();
    await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(embedToken ? { 'X-DreamBot-Token': embedToken } : {}),
      },
      body: JSON.stringify(payload),
      keepalive: true,
    });
    
    // Try to process any queued events
    processQueue();
  } catch {
    // If sending fails, queue it for later
    saveToQueue(payload);
  }
}

(globalThis as any).DreamBotTrackEvent = trackEvent;
